/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { InvoiceFilter, InvoiceTotals } from "./types";
import { money } from "./format";

type Props = {
  totals: InvoiceTotals;
  filter: InvoiceFilter;
  onFilter: (filter: InvoiceFilter) => void;
};

// What's owed at a glance; each tile filters the list.
export const SummaryTiles = defineComponent<Props>({
  name: "InvoiceSummaryTiles",
  props: ["totals", "filter", "onFilter"],
  setup(props) {
    const { t } = useLocalization();

    const tile = (
      filter: InvoiceFilter,
      label: string,
      amount: number,
      hint: string,
      tone: "neutral" | "error" | "warning" | "success",
    ) => {
      const active = props.filter === filter;
      const toneCls = {
        neutral: "text-base-content",
        error: amount > 0 ? "text-error" : "text-base-content",
        warning: amount > 0 ? "text-warning" : "text-base-content",
        success: "text-success",
      }[tone];
      return (
        <button
          type="button"
          class={[
            "flex flex-col items-start rounded-xl border bg-base-100 p-4 text-left transition",
            active ? "border-primary ring-2 ring-primary/20" : "border-base-300 hover:border-base-content/20",
          ].join(" ")}
          onClick={() => props.onFilter(active ? null : filter)}
        >
          <span class="text-xs font-medium uppercase tracking-wide text-base-content/50">{label}</span>
          <span class={`mt-1 text-2xl font-semibold tabular-nums ${toneCls}`}>{money(amount)}</span>
          <span class="mt-0.5 text-xs text-base-content/50">{hint}</span>
        </button>
      );
    };

    return () => {
      const s = props.totals;
      return (
        <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {tile("open", t("inventory.invoices.tile_open"), s.open_total,
            t("inventory.invoices.tile_count").replace("{count}", String(s.open_count)), "neutral")}
          {tile("overdue", t("inventory.invoices.tile_overdue"), s.overdue_total,
            t("inventory.invoices.tile_count").replace("{count}", String(s.overdue_count)), "error")}
          {tile("open", t("inventory.invoices.tile_due_soon"), s.due_soon_total,
            t("inventory.invoices.tile_due_soon_hint"), "warning")}
          {tile("paid", t("inventory.invoices.tile_paid"), s.paid_30d_total,
            t("inventory.invoices.tile_paid_hint"), "success")}
        </div>
      );
    };
  },
});
